import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { signIn, isCoach, getIdToken, resendConfirmationCode, currentUsername, signOut } from "../lib/auth";

/**
 * Where to land after signing in.
 *
 * `RequireAuth` records the page the user was trying to reach before it bounced them here, and its
 * comment has always claimed that signing back in returns them there. It did not: this page always
 * went to the role's default. So an expired session on a client's messages page dropped the coach on
 * the dashboard, and whatever they were doing was theirs to find again.
 *
 * Only same-origin app paths are honoured. `state` is attacker-influencable via a crafted link, and
 * following an absolute URL out of it would turn the sign-in page into an open redirect.
 */
function safeRedirect(from: unknown, fallback: string): string {
  if (typeof from !== "string") return fallback;
  // Must be a single-slash-rooted path: "//evil.example" and "https://evil.example" are both rejected.
  if (!from.startsWith("/") || from.startsWith("//")) return fallback;
  // Never bounce back to an auth screen -- that is a loop, not a destination.
  if (from === "/" || from.startsWith("/signin") || from.startsWith("/signup") || from.startsWith("/forgot-password")) {
    return fallback;
  }
  return from;
}

export function SignIn() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const navigate = useNavigate();
  // Router location, NOT window.location -- the remembered destination lives in router state, and
  // window.location has no `state`, so getting this wrong would silently always take the fallback.
  const location = useLocation();
  const from = (location.state as { from?: unknown } | null)?.from;

  // Without this, "/" always renders the login form regardless of whether a Cognito session is
  // still valid -- so browser back-navigation onto "/" (e.g. from a client's Messages page) looked
  // like the user had been signed out, even though their session was still live in local storage.
  /**
   * An existing session is offered, not imposed.
   *
   * This used to navigate straight into the app, which is right when you have arrived by accident and
   * wrong when you have come here on purpose: on a shared family tablet with a child's account live,
   * there was no route to the sign-in form at all short of hunting for Sign out (UX review F91). So the
   * page now says whose session it is and offers both directions.
   *
   * `from` still wins when it is set, because arriving via a redirect from a protected page IS the
   * accidental case -- the user asked for that page, not for this one.
   */
  const [existingSession, setExistingSession] = useState<{ destination: string; username: string | null } | null>(null);
  useEffect(() => {
    let cancelled = false;
    getIdToken().then((token) => {
      if (cancelled || !token) return;
      isCoach().then((coach) => {
        if (cancelled) return;
        const destination = safeRedirect(from, coach ? "/dashboard" : "/family");
        if (from) {
          navigate(destination, { replace: true });
          return;
        }
        setExistingSession({ destination, username: currentUsername() });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [navigate, from]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNeedsConfirmation(false);
    setSubmitting(true);
    try {
      await signIn(username, password);
      navigate(safeRedirect(from, (await isCoach()) ? "/dashboard" : "/family"));
    } catch (err) {
      // A user who signed up but never entered their emailed code is stuck here forever otherwise: the
      // password is right, Cognito refuses, and nothing on screen says a code is what's missing.
      if ((err as { code?: string })?.code === "UserNotConfirmedException") {
        setNeedsConfirmation(true);
        setError("Your account isn't confirmed yet. Check your email for the code we sent when you signed up.");
      } else {
        setError(err instanceof Error ? err.message : "Sign in failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendCode() {
    setError(null);
    try {
      await resendConfirmationCode(username);
      setError("We've sent a new confirmation code to your email.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resend the code.");
    }
  }

  if (existingSession) {
    return (
      <div className="centered-page">
        <div className="card">
          <h1>Athlete&rsquo;s Advantage</h1>
          <p className="form-hint">
            You&rsquo;re already signed in{existingSession.username ? ` as ${existingSession.username}` : ""}.
          </p>
          <button type="button" onClick={() => navigate(existingSession.destination, { replace: true })}>
            Continue
          </button>
          {/* The whole point of this screen existing rather than redirecting: a shared family tablet
              needs a way to become someone else. */}
          <button
            type="button"
            className="chip"
            onClick={() => {
              signOut();
              setExistingSession(null);
            }}
          >
            Sign in as someone else
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="centered-page">
      <form className="card" onSubmit={handleSubmit}>
        <h1>Athlete&rsquo;s Advantage</h1>
        <label htmlFor="username">Username or email</label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          required
        />
        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        {needsConfirmation && (
          <button type="button" className="chip" onClick={handleResendCode}>
            Resend confirmation code
          </button>
        )}
        <button type="submit" disabled={submitting}>
          {submitting ? "Signing in…" : "Sign in"}
        </button>
        <Link className="link-button" to="/forgot-password">
          Forgot your password?
        </Link>
      </form>
    </div>
  );
}
